import { useEffect, useMemo, useState } from 'react';
import { useLocalStorageByUser } from 'common/hooks';
import { DEFAULT_JOBS_COLOR_PALETTE, JOBS_COLOR_PALETTE_KEY, JOBS_COLOR_PALETTE_PRESETS_KEY, JobsColorPalette } from 'dashboard409a/pages/Workboard/common/constants';

export type ColorPalettePreset = {
  name: string;
  palette: JobsColorPalette;
};

let cachedJobsColorPalette: JobsColorPalette = DEFAULT_JOBS_COLOR_PALETTE;

export const getJobsColorPalette = (): JobsColorPalette => cachedJobsColorPalette;

/**
 * Custom hook for managing color palette state and operations
 * @returns Object containing colorPalette, presets, and handler functions
 */
export const useJobsColorPalette = () => {
  const [storedColorPalette, setColorPalette] = useLocalStorageByUser<JobsColorPalette>(
    JOBS_COLOR_PALETTE_KEY,
    DEFAULT_JOBS_COLOR_PALETTE
  );
  const colorPalette = useMemo(
    () => ({
      ...DEFAULT_JOBS_COLOR_PALETTE,
      ...storedColorPalette,
    }),
    [storedColorPalette]
  );
  const [presets, setPresets] = useLocalStorageByUser<ColorPalettePreset[]>(
    JOBS_COLOR_PALETTE_PRESETS_KEY,
    []
  );
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState('');

  useEffect(() => {
    // Keep non-hook status color helpers aligned with the active user.
    cachedJobsColorPalette = colorPalette;
  }, [colorPalette]);

  const handleColorChange = (key: keyof JobsColorPalette, color: string) => {
    setColorPalette(prev => {
      const next = { ...prev, [key]: color };
      cachedJobsColorPalette = next;
      return next;
    });
  };

  const handleReset = () => {
    cachedJobsColorPalette = DEFAULT_JOBS_COLOR_PALETTE;
    setColorPalette(DEFAULT_JOBS_COLOR_PALETTE);
  };

  const handleSavePreset = () => {
    if (presetName.trim()) {
      const existingIndex = presets.findIndex(p => p.name === presetName.trim());
      const updatedPresets = existingIndex >= 0
        ? presets.map((p, idx) => idx === existingIndex ? { name: presetName.trim(), palette: colorPalette } : p)
        : [...presets, { name: presetName.trim(), palette: colorPalette }];
      setPresets(updatedPresets);
      setPresetName('');
      setSavePresetOpen(false);
    }
  };

  const handleOpenSavePreset = () => {
    setSavePresetOpen(true);
  };

  const handleCloseSavePreset = () => {
    setSavePresetOpen(false);
    setPresetName('');
  };

  const handleApplyPreset = (name: string) => {
    const preset = presets.find(p => p.name === name);
    if (preset) {
      setColorPalette(preset.palette);
      cachedJobsColorPalette = preset.palette;
    }
  };

  const handleDeletePreset = (name: string) => {
    setPresets(presets.filter(p => p.name !== name));
  };

  return {
    colorPalette,
    presets,
    savePresetOpen,
    presetName,
    setPresetName,
    handleColorChange,
    handleReset,
    handleSavePreset,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleApplyPreset,
    handleDeletePreset,
  };
};
